import { z } from 'zod';

import { PayService } from '@/domain/pay';
import { TranslationsFullPath } from '@/domain/translations';
import { isValidAccountNumber } from '@/utils/account-utils';
import { sanitizeNumericValue } from '@/utils/text-format-utils';

const payeeAccountNumberInvalidPath: TranslationsFullPath = 'pay.validation.payeeAccountNumber.invalid';
const payeeAccountNumberRequiredPath: TranslationsFullPath = 'pay.validation.payeeAccountNumber.required';

const amountMinValuePath: TranslationsFullPath = 'pay.validation.amount.minValue';

const PayeeAccountNumberSchema = z
    .string({ message: payeeAccountNumberRequiredPath })
    .min(1, payeeAccountNumberRequiredPath)
    .refine((val) => {
        const sanitized = sanitizeNumericValue(val);
        return isValidAccountNumber(sanitized);
    }, payeeAccountNumberInvalidPath);

const amountExceedsFundsPath: TranslationsFullPath = 'pay.validation.amount.noMoney';
export const accountOwnershipErrorPath: TranslationsFullPath = 'pay.validation.account.ownership';

const ReceiverAccountSchema = z
    .object({
        number: PayeeAccountNumberSchema,
        ownership: z.boolean(),
    })
    .superRefine((val, ctx) => {
        if (!val.ownership) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ['number'],
                message: accountOwnershipErrorPath,
            });
        }
    });

const ReceiverAccountSchemaBeta = z.object({
    number: PayeeAccountNumberSchema,
    ownership: z.boolean().optional(),
});

export const TransferSchema = (toggle = false) =>
    z
        .object({
            maxAmount: z.number().min(1),
            amount: z.number().min(100, amountMinValuePath),
            account: toggle ? ReceiverAccountSchema : ReceiverAccountSchemaBeta,
        })
        .superRefine((val, ctx) => {
            if (val.amount > 0 && val.amount + PayService.cost(val.amount) > val.maxAmount) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    path: ['amount'],
                    message: amountExceedsFundsPath,
                });
            }
        });

export type KbTransferForm = z.infer<ReturnType<typeof TransferSchema>>;
